/**
 * Turbodoktoren custom collection filters (OE, usage, car.ids).
 *
 * Responsibilities:
 * - Strip TD filter params for "clear all" links
 * - Remove individual TD filter chips
 * - OE empty fallback: Mercedes A-prefix ↔ bare digits, then usage_numbers
 * - Client-side guard when Liquid marks a metafield filter as broken
 *
 * @see docs/SUCHE_UND_FILTER.md
 */
(function () {
  const TD_PARAMS = [
    "filter.p.m.custom.oe_numbers",
    "filter.p.m.custom.usage_numbers",
    "filter.v.m.car.ids",
    "_td_tried",
    "_td_kba",
    "_td_make",
    "_td_oe_q",
  ];

  function partNumberKey(value) {
    return String(value || "")
      .replace(/[\s/.-]/g, "")
      .toUpperCase();
  }

  function isNumericOnly(value) {
    return /^\d+$/.test(partNumberKey(value));
  }

  /** Part-number shape used for oe empty fallback (numeric or alphanumeric). */
  function isPartNumberFilterValue(value) {
    const key = partNumberKey(value);
    return key.length >= 5 && /\d/.test(key) && /^[A-Z0-9]+$/.test(key);
  }

  /** Pure digits ≥7 — candidates for Mercedes OE with/without leading A. */
  function isMercedesOeDigitBody(key) {
    return /^\d{7,}$/.test(key);
  }

  function isMercedesAPrefixedOe(key) {
    return /^A\d{7,}$/.test(key);
  }

  function buildClearFiltersParams(search) {
    const params = new URLSearchParams(search || window.location.search);
    TD_PARAMS.forEach((key) => params.delete(key));
    params.delete("page");
    return params;
  }

  function buildClearFiltersUrl(pathname) {
    const params = buildClearFiltersParams();
    const qs = params.toString();
    const path = pathname || window.location.pathname;
    return path + (qs ? "?" + qs : "");
  }

  function removeFilterParam(filterType) {
    const params = new URLSearchParams(window.location.search);
    if (filterType === "oe") {
      params.delete("filter.p.m.custom.oe_numbers");
      params.delete("_td_oe_q");
    } else if (filterType === "usage") {
      params.delete("filter.p.m.custom.usage_numbers");
      params.delete("_td_oe_q");
    } else if (filterType === "car") {
      params.delete("filter.v.m.car.ids");
      params.delete("_td_make");
    } else if (filterType === "kba") {
      params.delete("filter.v.m.car.ids");
      params.delete("_td_kba");
    }
    params.delete("page");
    params.delete("_td_tried");
    return params.toString();
  }

  /**
   * Facet forms (price, availability, …) do not include TD custom params — preserve them on submit.
   */
  function mergeTdFilterParams(searchParams) {
    const params = new URLSearchParams(searchParams || "");
    const current = new URLSearchParams(window.location.search);

    [
      "filter.p.m.custom.oe_numbers",
      "filter.p.m.custom.usage_numbers",
      "filter.v.m.car.ids",
      "_td_kba",
      "_td_make",
      "_td_oe_q",
      "type",
      "options[prefix]",
      "q",
    ].forEach((key) => {
      if (params.has(key)) return;
      current.getAll(key).forEach((value) => params.append(key, value));
    });

    if (/\/search\/?$/.test(window.location.pathname) && !params.has("type")) {
      params.set("type", "product");
    }

    params.delete("page");
    return params.toString();
  }

  function buildClearAllParams(href) {
    const current = new URLSearchParams(window.location.search);
    const sortBy = current.get("sort_by");

    if (href && href !== "#") {
      try {
        const target = new URL(href, window.location.origin);
        const params = new URLSearchParams(target.search);
        params.delete("page");
        if (sortBy && !params.has("sort_by")) {
          params.set("sort_by", sortBy);
        }
        return params.toString();
      } catch (e) {
        /* fall through */
      }
    }

    const params = buildClearFiltersParams();
    if (sortBy && !params.has("sort_by")) {
      params.set("sort_by", sortBy);
    }
    return params.toString();
  }

  function navigateOrRender(searchParams) {
    const FacetForm = window.FacetFiltersForm;
    const grid = document.getElementById("ProductGridContainer");
    if (FacetForm && typeof FacetForm.renderPage === "function" && grid) {
      FacetForm.renderPage(searchParams);
      return;
    }
    const qs = searchParams ? "?" + searchParams : "";
    window.location.href = window.location.pathname + qs + window.location.hash;
  }

  /**
   * When oe_numbers yields 0 results:
   *   1. Mercedes A-prefix: 6510901980 ↔ A6510901980 (_td_tried=a)
   *   2. Then usage_numbers once (_td_tried=usage)
   * Legacy URLs with _td_tried=oe on empty usage recover via step 1.
   */
  function buildOeToUsageFallbackParams(search) {
    const params = new URLSearchParams(search || window.location.search);
    const oeValues = params.getAll("filter.p.m.custom.oe_numbers");
    const oe = oeValues[0] || "";
    const usage = params.get("filter.p.m.custom.usage_numbers");
    const tried = params.get("_td_tried");

    // Partial/"contains" multi-value OE results — do not rewrite.
    if (params.get("_td_oe_q") || oeValues.length > 1) return null;

    if (tried === "usage") return null;

    // Stuck on usage after OE miss (legacy _td_tried=oe skipped A-prefix).
    if (usage && !oe && tried === "oe") {
      const usageKey = partNumberKey(usage);
      if (!isMercedesOeDigitBody(usageKey)) return null;
      params.delete("filter.p.m.custom.usage_numbers");
      params.set("filter.p.m.custom.oe_numbers", "A" + usageKey);
      params.set("_td_tried", "a");
      params.delete("page");
      return params.toString();
    }

    if (!oe || usage || !isPartNumberFilterValue(oe)) return null;

    const key = partNumberKey(oe);

    // Before usage: try A-prefix or strip leading A (once).
    if (tried !== "a") {
      if (isMercedesOeDigitBody(key)) {
        params.set("filter.p.m.custom.oe_numbers", "A" + key);
        params.set("_td_tried", "a");
        params.delete("page");
        return params.toString();
      }
      if (isMercedesAPrefixedOe(key)) {
        params.set("filter.p.m.custom.oe_numbers", key.slice(1));
        params.set("_td_tried", "a");
        params.delete("page");
        return params.toString();
      }
    }

    const usageKey = isMercedesAPrefixedOe(key) ? key.slice(1) : key;
    params.delete("filter.p.m.custom.oe_numbers");
    params.set("filter.p.m.custom.usage_numbers", usageKey);
    params.set("_td_tried", "usage");
    params.delete("page");
    return params.toString();
  }

  function responseHtmlIsEmptyCollection(html) {
    const parsed =
      typeof html === "string"
        ? new DOMParser().parseFromString(html, "text/html")
        : html;
    const grid = parsed.getElementById("product-grid");
    return grid?.classList.contains("collection--empty") ?? false;
  }

  function getOeToUsageFallbackParamsIfEmpty(html) {
    if (!responseHtmlIsEmptyCollection(html)) return null;
    return buildOeToUsageFallbackParams();
  }

  function applyOeToUsageFallback() {
    const params = buildOeToUsageFallbackParams();
    if (!params) return false;

    const grid = document.getElementById("product-grid");
    if (!grid?.classList.contains("collection--empty")) return false;

    const FacetForm = window.FacetFiltersForm;
    if (FacetForm && typeof FacetForm.renderPage === "function" && document.getElementById("ProductGridContainer")) {
      FacetForm.renderPage(params);
      return true;
    }

    const qs = params ? "?" + params : "";
    window.location.replace(window.location.pathname + qs + window.location.hash);
    return true;
  }

  let preservedAccordionState = null;

  const VEHICLE_ACCORDION_IDS = [
    "FacetVehicleSearch-facets",
    "FacetVehicleSearch-mobile",
    "FacetKbaSearch-facets",
    "FacetKbaSearch-mobile",
    "FacetOeSearch-facets",
    "FacetOeSearch-mobile",
  ];

  function isAccordionOpen(id) {
    const accordion = document.getElementById(id);
    if (!accordion) return false;
    const details = accordion.querySelector("details");
    return details?.open ?? false;
  }

  function setAccordionOpen(id, open) {
    const accordion = document.getElementById(id);
    if (!accordion) return;

    const details = accordion.querySelector("details");
    if (!details) return;

    const panel = details.querySelector(".custom-accordion__panel");
    details.classList.remove("closing");
    panel?.removeAttribute("style");
    details.open = open;
  }

  function captureAccordionOpenState() {
    const state = {};
    VEHICLE_ACCORDION_IDS.forEach((id) => {
      state[id] = isAccordionOpen(id);
    });
    return state;
  }

  function restoreAccordionOpenState(state) {
    if (!state) return;
    VEHICLE_ACCORDION_IDS.forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(state, id)) {
        setAccordionOpen(id, state[id]);
      }
    });
  }

  function finalizePreservedAccordionState() {
    preservedAccordionState = null;
    updateStickySidebar();
  }

  /** Extra top offset matching herrlich.scss sticky `top` (1.5rem + 50px). */
  const STICKY_TOP_EXTRA_REM = 1.5;
  const STICKY_TOP_EXTRA_PX = 50;

  let stickySidebarRaf = 0;
  let stickySidebarObservers = [];
  let stickySidebarBound = false;

  function getRemPx() {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  }

  function getHeaderHeightPx() {
    const styles = getComputedStyle(document.documentElement);
    return (
      parseFloat(styles.getPropertyValue("--header-height-live")) ||
      parseFloat(styles.getPropertyValue("--header-height")) ||
      0
    );
  }

  function getStickyTopPx() {
    return getHeaderHeightPx() + STICKY_TOP_EXTRA_REM * getRemPx() + STICKY_TOP_EXTRA_PX;
  }

  /** Space below sticky top: viewport − top offset. */
  function getStickyAvailableHeight() {
    return window.innerHeight - getStickyTopPx();
  }

  function getSidebarNaturalHeight(wrapper) {
    const sidebar = wrapper.querySelector(".facets-sidebar");
    if (sidebar) return sidebar.scrollHeight;
    return wrapper.scrollHeight;
  }

  function updateStickySidebar() {
    const layout = document.querySelector(".facets-vertical");
    const wrapper = layout?.querySelector(":scope > .facets-wrapper");
    const products = layout?.querySelector(":scope > #ProductGridContainer");

    if (!wrapper || !products) return;

    if (window.matchMedia("(max-width: 749.98px)").matches) {
      wrapper.classList.remove("facets-wrapper--sticky", "facets-wrapper--sticky-scroll");
      return;
    }

    const naturalHeight = getSidebarNaturalHeight(wrapper);
    const productHeight = products.offsetHeight;
    const availableHeight = getStickyAvailableHeight();
    const stickyTop = getStickyTopPx();
    const wasSticky = wrapper.classList.contains("facets-wrapper--sticky");
    const rect = wrapper.getBoundingClientRect();
    // Already pinned: unsticking would teleport the bar out of view.
    const isPinnedInView = wasSticky && rect.top <= stickyTop + 2;

    const productsTaller = productHeight > naturalHeight && naturalHeight > 0;
    const fitsInViewport = naturalHeight < availableHeight;

    let stick = false;
    let scroll = false;

    if (productsTaller && fitsInViewport) {
      stick = true;
    } else if (productsTaller && !fitsInViewport && isPinnedInView) {
      stick = true;
      scroll = true;
    }

    wrapper.classList.toggle("facets-wrapper--sticky", stick);
    wrapper.classList.toggle("facets-wrapper--sticky-scroll", stick && scroll);
  }

  function scrollOpenedFilterIntoSidebar(details) {
    const wrapper = details?.closest?.(".facets-vertical > .facets-wrapper");
    if (!wrapper?.classList.contains("facets-wrapper--sticky-scroll")) return;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    requestAnimationFrame(() => {
      details.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  }

  function scheduleStickySidebarUpdate() {
    if (stickySidebarRaf) cancelAnimationFrame(stickySidebarRaf);
    stickySidebarRaf = requestAnimationFrame(() => {
      stickySidebarRaf = 0;
      updateStickySidebar();
    });
  }

  function disconnectStickySidebarObservers() {
    stickySidebarObservers.forEach((observer) => observer.disconnect());
    stickySidebarObservers = [];
  }

  function observeStickySidebarTargets() {
    disconnectStickySidebarObservers();

    const layout = document.querySelector(".facets-vertical");
    const wrapper = layout?.querySelector(":scope > .facets-wrapper");
    const products = layout?.querySelector(":scope > #ProductGridContainer");
    if (!wrapper || !products || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(scheduleStickySidebarUpdate);
    observer.observe(wrapper);
    observer.observe(products);
    const sidebar = wrapper.querySelector(".facets-sidebar");
    if (sidebar) observer.observe(sidebar);
    stickySidebarObservers.push(observer);
  }

  function bindStickySidebar() {
    if (stickySidebarBound) {
      observeStickySidebarTargets();
      scheduleStickySidebarUpdate();
      return;
    }
    stickySidebarBound = true;

    window.addEventListener("resize", scheduleStickySidebarUpdate, { passive: true });

    document.addEventListener(
      "toggle",
      (event) => {
        if (!(event.target instanceof HTMLDetailsElement)) return;
        if (!event.target.closest(".facets-vertical > .facets-wrapper")) return;
        scheduleStickySidebarUpdate();
        setTimeout(() => {
          updateStickySidebar();
          scrollOpenedFilterIntoSidebar(event.target);
        }, 480);
      },
      true
    );

    document.addEventListener(
      "transitionend",
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest(".facets-vertical > .facets-wrapper custom-accordion")) return;
        if (event.propertyName && event.propertyName !== "height") return;
        scheduleStickySidebarUpdate();
      },
      true
    );

    observeStickySidebarTargets();
    scheduleStickySidebarUpdate();
  }

  const vehicleChipLabelCache = Object.create(null);

  function getActiveCarId() {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("filter.v.m.car.ids");
    if (fromUrl) return fromUrl.trim();
    return (document.body?.dataset?.tdUrlCar || "").trim();
  }

  function updateVehicleChipLabels(label) {
    if (!label) return;
    document.querySelectorAll(".js-td-vehicle-chip-label").forEach((el) => {
      el.textContent = label;
    });
  }

  function waitForVehicleGarage(maxAttempts = 20) {
    return new Promise((resolve) => {
      let attempts = 0;
      const tick = () => {
        if (window.VehicleGarage?.resolveLabelForCarId) {
          resolve(window.VehicleGarage);
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          resolve(null);
          return;
        }
        setTimeout(tick, 50);
      };
      tick();
    });
  }

  async function resolveVehicleChipLabels() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("_td_kba")) return;

    const carId = getActiveCarId();
    if (!carId) return;

    const chips = document.querySelectorAll(".js-td-vehicle-chip-label");
    if (!chips.length) return;

    const cached = vehicleChipLabelCache[carId];
    if (cached) {
      updateVehicleChipLabels(cached);
      return;
    }

    const garage = await waitForVehicleGarage();
    if (!garage?.resolveLabelForCarId) return;

    const label = await garage.resolveLabelForCarId(carId);
    if (label && label !== carId) {
      vehicleChipLabelCache[carId] = label;
      updateVehicleChipLabels(label);
    }
  }

  function syncVehicleSearchPanels(sourceHtml) {
    const parsed =
      typeof sourceHtml === "string"
        ? new DOMParser().parseFromString(sourceHtml, "text/html")
        : sourceHtml;
    if (!parsed) return;

    const accordionIds = [
      "FacetVehicleSearch-facets",
      "FacetVehicleSearch-mobile",
      "FacetKbaSearch-facets",
      "FacetKbaSearch-mobile",
      "FacetOeSearch-facets",
      "FacetOeSearch-mobile",
    ];

    accordionIds.forEach((id) => {
      const source = parsed.getElementById(id);
      const target = document.getElementById(id);
      if (!source || !target) return;

      target.innerHTML = source.innerHTML;
      if (typeof target.reinit === "function") target.reinit();
    });

    if (preservedAccordionState) {
      restoreAccordionOpenState(preservedAccordionState);
    }

    document.querySelectorAll("facet-filters-form custom-accordion.facets-vehicle-search").forEach((accordion) => {
      if (typeof accordion.reinit === "function") accordion.reinit();
      if (preservedAccordionState && accordion.id) {
        const open = preservedAccordionState[accordion.id];
        if (typeof open === "boolean") setAccordionOpen(accordion.id, open);
      }
    });

    resolveVehicleChipLabels();
  }

  function openVehicleFilterAccordions() {
    if (preservedAccordionState) {
      restoreAccordionOpenState(preservedAccordionState);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const oe = params.get("filter.p.m.custom.oe_numbers");
    const usage = params.get("filter.p.m.custom.usage_numbers");
    const car = params.get("filter.v.m.car.ids");
    const kba = params.get("_td_kba");

    const openAccordion = (id) => setAccordionOpen(id, true);
    const closeAccordion = (id) => setAccordionOpen(id, false);

    if (oe || usage) {
      closeAccordion("FacetVehicleSearch-facets");
      closeAccordion("FacetVehicleSearch-mobile");
      openAccordion("FacetOeSearch-facets");
      openAccordion("FacetOeSearch-mobile");
    } else if (kba) {
      closeAccordion("FacetVehicleSearch-facets");
      closeAccordion("FacetVehicleSearch-mobile");
      closeAccordion("FacetOeSearch-facets");
      closeAccordion("FacetOeSearch-mobile");
      openAccordion("FacetKbaSearch-facets");
      openAccordion("FacetKbaSearch-mobile");
    } else if (car) {
      closeAccordion("FacetOeSearch-facets");
      closeAccordion("FacetOeSearch-mobile");
      openAccordion("FacetVehicleSearch-facets");
      openAccordion("FacetVehicleSearch-mobile");
    } else {
      closeAccordion("FacetOeSearch-facets");
      closeAccordion("FacetOeSearch-mobile");
      closeAccordion("FacetKbaSearch-facets");
      closeAccordion("FacetKbaSearch-mobile");
      openAccordion("FacetVehicleSearch-facets");
      openAccordion("FacetVehicleSearch-mobile");
    }
  }

  function markGridFilterBroken(grid) {
    const wrapper = document.getElementById("ProductGridContainer")?.querySelector(
      ".collection:not(.collection--empty)",
    );
    if (wrapper) wrapper.hidden = true;
    if (grid) {
      grid.classList.add("collection--empty");
      grid.setAttribute("data-td-filter-broken", "");
    }
    return true;
  }

  /**
   * Client-side fallback when usage/oe param is set but the grid still shows an unfiltered catalog.
   * Must not treat successful /search?q=* filter hits ("N Ergebnis(se)") as broken.
   */
  function detectBrokenFilterFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const usage = params.get("filter.p.m.custom.usage_numbers");
    const oe = params.get("filter.p.m.custom.oe_numbers");
    if (!usage && !oe) return false;

    const grid = document.getElementById("product-grid");
    if (grid?.classList.contains("collection--empty")) return false;

    const items =
      grid?.querySelectorAll("#product-grid .grid__item, .product-grid .grid__item") || [];
    if (items.length === 0) return false;

    const countText =
      document.querySelector(".js-product-count")?.textContent?.replace(/\s+/g, " ").trim() || "";

    // Search results copy: "1 Ergebnis" / "12 Ergebnisse" → filter applied, not broken.
    if (/\d+\s+Ergebnisse?\b/i.test(countText)) return false;

    // "X von Y" with X !== Y → narrowed, not broken.
    const rangeMatch = countText.match(/(\d+)\s*(?:von|of)\s*(\d+)/i);
    if (rangeMatch) {
      if (rangeMatch[1] !== rangeMatch[2]) return false;
      return markGridFilterBroken(grid);
    }

    // "N Produkte" on a collection where filters are disabled / catalog is huge → ignored filter.
    const singleCountMatch = countText.match(/^(\d+)\s+(?:Produkte|products?)\b/i);
    if (singleCountMatch) {
      const n = Number(singleCountMatch[1]);
      const filtersEl = document.getElementById("main-collection-filters");
      const allCount = Number(filtersEl?.dataset?.tdAllProductsCount || 0);
      const filtersUnavailable = filtersEl?.dataset?.tdFiltersAvailable === "false";
      if (filtersUnavailable || allCount > 5000 || n > 5000) {
        return markGridFilterBroken(grid);
      }
      if (allCount > 0 && n === allCount) return markGridFilterBroken(grid);
      return false;
    }

    return false;
  }

  /**
   * Hide product grid when Liquid detected a broken metafield filter but products still render.
   */
  function detectBrokenMetafieldFilter() {
    if (detectBrokenFilterFromUrl()) return;
    const root = document.querySelector("[data-td-filter-broken]");
    if (!root) return;

    const grid = document.getElementById("product-grid");
    if (grid?.classList.contains("collection--empty")) return;

    const productCards = document.querySelectorAll("#product-grid .grid__item");
    if (productCards.length === 0) return;

    root.classList.add("collection--empty");
    root.querySelector(".loading-overlay")?.classList.remove("hidden");

    const container = document.getElementById("ProductGridContainer");
    const existingGrid = container?.querySelector(".collection:not(.collection--empty)");
    if (existingGrid) existingGrid.hidden = true;
  }

  function onChipRemoveClick(event) {
    const link = event.target.closest(".js-td-filter-chip-remove");
    if (!link) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const filterType = link.getAttribute("data-td-remove-filter");
    if (!filterType) return;
    preservedAccordionState = captureAccordionOpenState();
    navigateOrRender(removeFilterParam(filterType));
  }

  function onClearAllClick(event) {
    const link = event.target.closest(
      ".js-td-clear-all-filters, .td-active-facets-bar .active-facets__button-remove",
    );
    if (!link) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    preservedAccordionState = captureAccordionOpenState();
    navigateOrRender(buildClearAllParams(link.getAttribute("href")));
  }

  /**
   * Shopify disables collection filters above 5,000 products. Old bookmarks like
   * /collections/all?filter.p.m.custom.oe_numbers=… must move to /search?q=*.
   */
  function migrateCollectionFilterToSearch() {
    const path = String(window.location.pathname || "");
    if (!/\/collections\//.test(path)) return false;

    const params = new URLSearchParams(window.location.search);
    const hasTdFilter =
      params.has("filter.p.m.custom.oe_numbers") ||
      params.has("filter.p.m.custom.usage_numbers") ||
      params.has("filter.v.m.car.ids");
    if (!hasTdFilter) return false;

    const filtersEl = document.getElementById("main-collection-filters");
    const filtersAvailable = filtersEl?.dataset?.tdFiltersAvailable !== "false";
    const allCount = Number(filtersEl?.dataset?.tdAllProductsCount || 0);
    const overLimit = allCount > 5000;

    if (filtersAvailable && !overLimit) return false;

    params.set("q", "*");
    params.set("type", "product");
    if (!params.has("options[prefix]")) params.set("options[prefix]", "last");

    const root = window.Shopify?.routes?.root || "/";
    window.location.replace(`${root}search?${params.toString()}`);
    return true;
  }

  function init() {
    document.addEventListener("click", onChipRemoveClick, true);
    document.addEventListener("click", onClearAllClick, true);

    if (migrateCollectionFilterToSearch()) return;

    if (!applyOeToUsageFallback()) {
      detectBrokenMetafieldFilter();
      openVehicleFilterAccordions();
      resolveVehicleChipLabels();
    }

    bindStickySidebar();
  }

  window.TdFilters = {
    TD_PARAMS,
    isNumericOnly,
    isPartNumberFilterValue,
    partNumberKey,
    isMercedesOeDigitBody,
    isMercedesAPrefixedOe,
    buildClearFiltersParams,
    buildClearAllParams,
    buildClearFiltersUrl,
    removeFilterParam,
    mergeTdFilterParams,
    navigateOrRender,
    applyOeToUsageFallback,
    buildOeToUsageFallbackParams,
    getOeToUsageFallbackParamsIfEmpty,
    responseHtmlIsEmptyCollection,
    detectBrokenMetafieldFilter,
    migrateCollectionFilterToSearch,
    detectBrokenFilterFromUrl,
    openVehicleFilterAccordions,
    syncVehicleSearchPanels,
    resolveVehicleChipLabels,
    finalizePreservedAccordionState,
    updateStickySidebar,
    bindStickySidebar,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
